import {
  useErpTeacherSurveyFeedbackQuery,
  useErpTeacherSurveyLeaderboardQuery,
} from 'core/graphql';

import { ITeacherLeaderboardRow, ITeacherSurveyReport } from './surveyModel';

/** Tone bands mirrored from `surveys.services.teacher_insight`. */
export const TEACHER_RATING_WEAK_BELOW = 3.5;
export const TEACHER_RATING_STRONG_FROM = 4.5;

export type TeacherRatingTone = 'weak' | 'ok' | 'strong' | 'unknown';

export const teacherRatingTone = (
  average?: number | null,
): TeacherRatingTone => {
  if (average === null || average === undefined) return 'unknown';
  if (average < TEACHER_RATING_WEAK_BELOW) return 'weak';
  if (average >= TEACHER_RATING_STRONG_FROM) return 'strong';
  return 'ok';
};

interface IUseTeacherSurveyFeedbackArgs {
  teacherId?: string;
  courseId?: string;
  groupId?: string;
  dateFrom?: string;
  dateTo?: string;
  bucket?: 'month' | 'quarter';
  /** Pass false for roles outside TEACHER_INSIGHT_ROLES — a denied query
   *  rejects the Apollo promise and would surface as a red toast. */
  enabled?: boolean;
}

interface ITeacherSurveyFeedbackHook {
  report?: ITeacherSurveyReport;
  loading: boolean;
}

export const useTeacherSurveyFeedback = ({
  teacherId,
  courseId,
  groupId,
  dateFrom,
  dateTo,
  bucket = 'month',
  enabled = true,
}: IUseTeacherSurveyFeedbackArgs): ITeacherSurveyFeedbackHook => {
  const { data, loading } = useErpTeacherSurveyFeedbackQuery({
    variables: {
      teacherId: teacherId as string,
      courseId,
      groupId,
      dateFrom,
      dateTo,
      bucket,
    },
    skip: !enabled || !teacherId,
    fetchPolicy: 'cache-and-network',
  });

  return { report: data?.teacherSurveyFeedback || undefined, loading };
};

interface IUseTeacherLeaderboardArgs {
  courseId?: string;
  dateFrom?: string;
  dateTo?: string;
  minResponses?: number;
  enabled?: boolean;
}

interface ITeacherLeaderboardHook {
  rows: ITeacherLeaderboardRow[];
  loading: boolean;
}

/**
 * Spec 0027 — every teacher with survey feedback, WEAKEST FIRST and windowed.
 * `Teacher.studentRating` cannot serve this: it is an all-time all-course
 * average and cannot answer «кто слаб на этом курсе в этом квартале».
 */
export const useTeacherSurveyLeaderboard = ({
  courseId,
  dateFrom,
  dateTo,
  minResponses,
  enabled = true,
}: IUseTeacherLeaderboardArgs): ITeacherLeaderboardHook => {
  const { data, loading } = useErpTeacherSurveyLeaderboardQuery({
    variables: { courseId, dateFrom, dateTo, minResponses },
    skip: !enabled,
    fetchPolicy: 'cache-and-network',
  });

  return { rows: data?.teacherSurveyLeaderboard || [], loading };
};
